// Pure, generic helpers for Group Chat. File classification / linkify /
// duration formatting are relocated (not reinvented) from schoolmate_dev's
// ChatPage.jsx, which the task explicitly calls out as reusable — they carry
// no schoolmate-specific API or styling assumptions.
import { parseApiDate } from '../../utils/format';

export const MAX_FILE_MB = 25;
export const MAX_FILE_SIZE = MAX_FILE_MB * 1024 * 1024;

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif'];
const AUDIO_EXTENSIONS = ['mp3', 'm4a', 'aac', 'ogg', 'oga', 'opus', 'wav', 'amr', 'weba'];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'webm', 'mkv', '3gp', 'avi'];
const DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'rtf', 'xml', 'json', 'zip'];
const ALLOWED_EXTENSIONS = new Set([...IMAGE_EXTENSIONS, ...AUDIO_EXTENSIONS, ...VIDEO_EXTENSIONS, ...DOCUMENT_EXTENSIONS]);
export const ALLOWED_ATTACHMENT_ACCEPT = [...ALLOWED_EXTENSIONS].map((e) => `.${e}`).join(',');

const getExtension = (name) => {
    const m = String(name || '').toLowerCase().match(/\.([a-z0-9]+)(?:\?|$)/);
    return m ? m[1] : '';
};

export const classifyFile = (fileNameOrUrl) => {
    const ext = getExtension(fileNameOrUrl);
    let category = 'document';
    if (IMAGE_EXTENSIONS.includes(ext)) category = 'image';
    else if (AUDIO_EXTENSIONS.includes(ext)) category = 'audio';
    else if (VIDEO_EXTENSIONS.includes(ext)) category = 'video';
    return { ext, category, allowed: ALLOWED_EXTENSIONS.has(ext) };
};

export const formatFileSize = (bytes) => {
    const n = Number(bytes) || 0;
    if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(n / 1024))} KB`;
};

export const formatDuration = (seconds) => {
    if (!seconds || Number.isNaN(seconds) || !Number.isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
};

/** Turns bare URLs in plain message text into clickable links. Returns an
 * array of strings/React elements suitable for direct JSX interpolation. */
export const linkify = (text, linkColor) => {
    const str = String(text || '');
    if (!str) return [str];
    return str.split(/(https?:\/\/[^\s]+|www\.[^\s]+)/gi).map((part, i) => {
        if (!part) return null;
        if (/^(https?:\/\/|www\.)/i.test(part)) {
            const href = /^https?:\/\//i.test(part) ? part : `https://${part}`;
            return { link: true, key: i, href, text: part, color: linkColor };
        }
        return part;
    });
};

// sentOn arrives as "dd-MM-yyyy HH:mm" in UTC — confirmed by messages
// displaying a time exactly India's UTC+5:30 behind the actual send time
// (e.g. a message sent at 4:38pm IST came back as "11:08"). parseApiDate
// (used app-wide, see utils/format.js) treats those digits as already-local,
// which is correct for other endpoints but wrong here, so this constructs
// the instant as UTC instead and lets the browser convert it to local time
// when displayed — a Date object (our own optimistic messages, before the
// server round trip) or an ISO string with an explicit offset is left alone,
// since both are already unambiguous.
export const parseSentOn = (sentOn) => {
    if (!sentOn) return null;
    if (sentOn instanceof Date) return Number.isNaN(sentOn.getTime()) ? null : sentOn;
    const s = String(sentOn).trim();
    const dmy = s.match(/^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
    if (dmy) {
        const [, d, mo, y, hh = '0', mi = '0', ss = '0'] = dmy;
        return new Date(Date.UTC(+y, +mo - 1, +d, +hh, +mi, +ss));
    }
    const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?(Z|[+-]\d{2}:?\d{2})?/);
    if (ymd && !ymd[7]) {
        const [, y, mo, d, hh = '0', mi = '0', ss = '0'] = ymd;
        return new Date(Date.UTC(+y, +mo - 1, +d, +hh, +mi, +ss));
    }
    return parseApiDate(s);
};

export const formatMessageTime = (sentOn) => {
    const d = parseSentOn(sentOn);
    return d ? d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }) : '';
};

export const formatDayDivider = (sentOn) => {
    const d = parseSentOn(sentOn);
    if (!d) return '';
    const today = new Date();
    const yest = new Date();
    yest.setDate(today.getDate() - 1);
    const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    if (sameDay(d, today)) return 'Today';
    if (sameDay(d, yest)) return 'Yesterday';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
};

export const EDIT_WINDOW_MS = 15 * 60 * 1000;

/** The message DTO doesn't carry an explicit edit-deadline field, so this
 * trusts sentOn + a fixed 15-minute window per the doc's stated server rule
 * ("sender only, within the 15-minute edit window") rather than the client
 * clock alone driving anything beyond disabling the action once past it. */
export const canEditMessage = (message) => {
    if (!message || message.isDeleted || message.messageType === 'system') return false;
    const sent = parseSentOn(message.sentOn);
    if (!sent) return false;
    return Date.now() - sent.getTime() < EDIT_WINDOW_MS;
};

const AVATAR_TONES = [
    { bgcolor: '#EDE9FE', color: '#6D28D9' },
    { bgcolor: '#E0F2FE', color: '#0369A1' },
    { bgcolor: '#FEF3C7', color: '#B45309' },
    { bgcolor: '#DCFCE7', color: '#15803D' },
    { bgcolor: '#FFE4E6', color: '#BE123C' },
    { bgcolor: '#E0E7FF', color: '#4338CA' },
    { bgcolor: '#CCFBF1', color: '#0F766E' },
    { bgcolor: '#FFEDD5', color: '#C2410C' },
];

export const avatarToneFor = (key) => {
    const s = String(key ?? '');
    let sum = 0;
    for (let i = 0; i < s.length; i += 1) sum += s.charCodeAt(i);
    return AVATAR_TONES[sum % AVATAR_TONES.length];
};

export const initialsFrom = (name) => String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const SYSTEM_EVENT_LABEL = {
    group_created: 'created the group',
    member_added: 'added members',
    member_removed: 'removed members',
    member_left: 'left the group',
    group_renamed: 'renamed the group',
    group_icon_changed: 'changed the group icon',
    role_changed: 'changed a member role',
    message_pinned: 'pinned a message',
    message_unpinned: 'unpinned a message',
};

export const systemEventLabel = (type) => SYSTEM_EVENT_LABEL[type] || '';

/** Sidebar/list preview text for a message DTO (ReceiveMessage payload or a
 * GetGroups lastMessage) — text messages show as-is, attachments fall back
 * to a type label since there's no caption to show in a one-line preview. */
export const previewTextFor = (msg) => {
    if (!msg) return '';
    if (msg.isDeleted) return 'This message was deleted';
    if (msg.messageType === 'system') return systemEventLabel(msg.systemEventType) || msg.messageText || 'Group updated';
    if (msg.messageText) return msg.messageText;
    if (msg.attachmentUrl || msg.attachmentName) {
        const { category } = classifyFile(msg.attachmentName || msg.attachmentUrl);
        if (category === 'image') return '📷 Photo';
        if (category === 'video') return '🎥 Video';
        if (category === 'audio') return '🎤 Voice message';
        return `📄 ${msg.attachmentName || 'Document'}`;
    }
    return '';
};

/** Normalizes a group DTO from GetGroups/GetGroupInfo into the shape the UI
 * relies on, defensively — the exact backend field names for these two
 * endpoints aren't given verbatim in the integration guide (only the message
 * DTO shape is), so this tolerates a few likely variants. */
export const normalizeGroup = (g) => ({
    groupId: g.groupId ?? g.id,
    name: g.name ?? g.groupName ?? '',
    description: g.description ?? '',
    iconUrl: g.iconUrl ?? g.icon ?? g.groupIcon ?? null,
    lastMessageText: g.lastMessageText ?? g.lastMessage?.messageText ?? '',
    lastMessageOn: g.lastMessageOn ?? g.lastMessage?.sentOn ?? null,
    lastMessageSenderName: g.lastMessageSenderName ?? g.lastMessage?.senderName ?? '',
    unreadCount: Number(g.unreadCount ?? g.unread ?? 0) || 0,
    isMuted: Boolean(g.isMuted ?? g.muted),
    mutedUntil: g.mutedUntil ?? null,
    memberCount: Number(g.memberCount ?? g.membersCount ?? 0) || 0,
    isCreator: Boolean(g.isCreator ?? g.createdByMe),
    creatorUserId: g.creatorUserId ?? g.createdByUserId ?? null,
    myRole: g.myRole ?? g.role ?? 'member',
    hasLeft: Boolean(g.hasLeft),
    isPinned: Boolean(g.isPinned),
});

/** Extracts a result array from a FetchMessages/SearchMessages/FetchMedia
 * response body. The doc never gives an explicit field name for any of these
 * (unlike the message DTO itself, which is fully specified) — `items` was
 * the original guess everywhere, but an endpoint named after its resource is
 * at least as likely to nest its array under that resource's own name
 * (schoolmate's own equivalent backend call uses exactly `messages`, for
 * example). Try the endpoint-specific name first, then the other likely
 * names, then a bare array, so a real backend response isn't silently read
 * as empty just because of a guessed key. */
const extractList = (body, preferredKey) => {
    const d = body?.data;
    if (preferredKey && Array.isArray(d?.[preferredKey])) return d[preferredKey];
    if (Array.isArray(d?.messages)) return d.messages;
    if (Array.isArray(d?.items)) return d.items;
    if (Array.isArray(d?.media)) return d.media;
    if (Array.isArray(d)) return d;
    return [];
};
export const extractMessageList = (body) => extractList(body, 'messages');
export const extractMediaList = (body) => extractList(body, 'media');

export const normalizeMember = (m) => ({
    userId: m.userId ?? m.id,
    name: m.name ?? m.userName ?? m.fullName ?? '',
    designation: m.designation ?? m.designationName ?? '',
    role: m.role ?? m.memberRole ?? 'member',
    isOnline: Boolean(m.isOnline ?? m.online),
    isCreator: Boolean(m.isCreator),
});

/** Copies text to the clipboard, falling back to a hidden-textarea + execCommand
 * for non-secure contexts / browsers without the async Clipboard API. */
export const copyTextToClipboard = async (text) => {
    const str = String(text ?? '');
    if (navigator.clipboard && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(str);
            return true;
        } catch {
            // fall through to the legacy method below
        }
    }
    try {
        const ta = document.createElement('textarea');
        ta.value = str;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '-9999px';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
    } catch {
        return false;
    }
};

/** A curated, self-contained emoji set (no external picker dependency) used
 * both to insert emoji into the composer and to pick a reaction beyond the
 * six quick-reaction shortcuts — mirrors schoolmate's emoji-picker-react
 * usage in spirit without adding a new runtime dependency. */
export const EMOJI_GROUPS = [
    {
        label: 'Smileys',
        emojis: ['😀', '😁', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😍', '🥰', '😘', '😋', '😛', '🤪', '😜', '🤔', '🤨', '😐', '😑', '😶', '🙄', '😏', '😴', '😪', '😌', '🤗', '🤩', '🥳', '😎'],
    },
    {
        label: 'Reactions',
        emojis: ['😢', '😭', '😤', '😠', '😡', '🤯', '😳', '🥺', '😱', '😨', '😰', '😥', '😓', '🤗', '🤭', '🤫', '🤥', '😷', '🤒', '🤕', '🥴', '🤢', '🤮', '🥵', '🥶'],
    },
    {
        label: 'Gestures',
        emojis: ['👍', '👎', '👏', '🙌', '👐', '🤝', '🙏', '💪', '✌️', '🤞', '🤟', '🤘', '👌', '👋', '🤙', '👆', '👇', '👈', '👉', '✋'],
    },
    {
        label: 'Symbols',
        emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💔', '💯', '✨', '🔥', '🎉', '🎊', '⭐', '🌟', '💥', '⚡', '✅', '❌', '❗', '❓', '💤', '🕒'],
    },
];

export const QUICK_REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
